import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  normalizeAccessToken,
  cacheTokenExpiry,
} from '@/utils/auth-token';
import { invalidateAuthSession } from '@/utils/auth-session';
import { resolveInitialLanguage } from '@/utils/app-language';
import { resolveApiUrl, withBackendPrefix } from '@/utils/backend-prefix';
import { useAnalyticsStore } from '@/stores/analytics';

function recordRequestAnalytics(config, { ok, code }) {
  if (!config) return;
  try {
    const started = Number(config.__analyticsStartedAt);
    const durationMs = Number.isFinite(started) ? Date.now() - started : 0;
    useAnalyticsStore().recordApi({
      method: config.method,
      url: config.url,
      ok,
      durationMs,
      code: code ?? null,
    });
  } catch {
    /* pinia 未就绪或统计失败时不阻断请求 */
  }
}

// 1. 创建一个新的 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 10000,
});

let currentAcceptLanguage = resolveInitialLanguage().acceptTag;

/** 仅更新请求头 / 内存态；不落盘。持久化仅由 switchAppLocale 调用 persistAcceptLanguage */
export function applyAcceptLanguageHeader(acceptTag) {
  const tag = String(acceptTag ?? '').trim();
  if (!tag) return;
  currentAcceptLanguage = tag;
  service.defaults.headers.common['Accept-Language'] = tag;
}

applyAcceptLanguageHeader(currentAcceptLanguage);

function resolveRequestAcceptLanguage(config) {
  return (
    config?.headers?.['Accept-Language'] ||
    service.defaults.headers.common['Accept-Language'] ||
    currentAcceptLanguage
  );
}

export function getCurrentAcceptLanguage() {
  return currentAcceptLanguage;
}

/** 联赛 API 请求标记：跳过 401 全局登出 + 开发环境 HTML 误返回提示 */
export const LEAGUES_REQUEST_CONFIG = {
  _skipAuthRefresh: true,
  _leaguesHtmlGuard: true,
};

function rejectLeaguesHtmlResponse() {
  return Promise.reject(
    new Error(
      '联赛 API 返回了页面 HTML，请用 npm run serve 启动开发服务，并确认 Network 请求为 /app/leagues/*'
    )
  );
}

function parseLeaguesStringBody(raw) {
  if (raw.includes('<!DOCTYPE html>') || raw.includes('<noscript>')) {
    return rejectLeaguesHtmlResponse();
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && isApiSuccess(parsed.code)) return parsed.data;
    return Promise.reject(
      new Error(parsed?.msg || parsed?.message || raw.slice(0, 120) || 'Request failed')
    );
  } catch {
    return Promise.reject(new Error(raw.slice(0, 120) || 'Request failed'));
  }
}

function formatLeaguesHttpError(error) {
  const status = error?.response?.status;
  const url = error?.config?.url || '';
  const msg = error?.response?.data?.msg || error?.message || 'Request failed';
  return Promise.reject(new Error(status ? `[${status}] ${url} ${msg}` : msg));
}

// ---- Token 刷新队列，防止并发 401 时重复刷新 ----
let isRefreshing = false;
let pendingQueue = []; // [{ resolve, reject }]

function flushQueue(newToken, error) {
  pendingQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(newToken)));
  pendingQueue = [];
}

/** RFC4122-ish id for X-Request-Id (ops / Loki correlation_id). */
function newClientRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  const hex = (n) => Math.floor(n).toString(16).padStart(2, '0');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = (Math.random() * 256) | 0;
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const b = [...bytes].map(hex).join('');
  return `${b.slice(0, 8)}-${b.slice(8, 12)}-${b.slice(12, 16)}-${b.slice(16, 20)}-${b.slice(20)}`;
}

function readResponseRequestId(headers) {
  if (!headers) return '';
  const get =
    typeof headers.get === 'function'
      ? (k) => headers.get(k)
      : (k) => headers[k] || headers[k.toLowerCase()];
  return String(get('x-request-id') || get('X-Request-Id') || '').trim();
}

function attachRequestId(error, config, responseHeaders) {
  if (!error || typeof error !== 'object') return error;
  const rid =
    readResponseRequestId(responseHeaders) ||
    String(config?.headers?.['X-Request-Id'] || config?.__requestId || '').trim();
  if (rid) {
    error.requestId = rid;
    error.traceId = rid;
  }
  return error;
}

async function doRefresh() {
  const accessToken = normalizeAccessToken(getAccessToken());
  const refreshToken = getRefreshToken() || accessToken;
  if (!refreshToken && !accessToken) throw new Error('no refresh token');
  const res = await axios.post(
    resolveApiUrl('/liyuan-auth/refresh'),
    { refreshToken },
    {
      headers: {
        'Accept-Language': currentAcceptLanguage,
        'X-Request-Id': newClientRequestId(),
        ...(accessToken ? { Authorization: accessToken } : {}),
      },
    }
  );
  const code = res.data?.code;
  if (code !== 0 && code !== 200) {
    throw new Error(res.data?.msg || 'refresh failed');
  }
  // 服务端续期 session；本地 expiresIn 同步延长 24h
  cacheTokenExpiry(accessToken, res.data?.data?.expiresIn ?? 1440);
  return accessToken;
}

function handleSessionExpired() {
  invalidateAuthSession({ redirectHome: true });
}

// 2. 请求拦截器
service.interceptors.request.use(
  (config) => {
    if (!config.headers) config.headers = {};
    if (!config._skipAuthHeader && !config.headers.Authorization) {
      const token = normalizeAccessToken(getAccessToken());
      if (token) config.headers.Authorization = token;
    }
    if (config.headers.Authorization) {
      // 避免 Authorization: Bearer Bearer xxx / 误存前缀
      config.headers.Authorization = normalizeAccessToken(config.headers.Authorization);
    }
    if (config._skipAuthHeader) {
      delete config.headers.Authorization;
    }
    config.headers['Accept-Language'] = resolveRequestAcceptLanguage(config);
    // Correlate FE → Go access log → business slog → outbox (Loki correlation_id / request_id).
    if (!config.headers['X-Request-Id']) {
      config.headers['X-Request-Id'] = newClientRequestId();
    }
    config.__requestId = config.headers['X-Request-Id'];
    if (config.url) {
      config.url = withBackendPrefix(config.url);
    }
    config.__analyticsStartedAt = Date.now();
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// 3. 响应拦截器
function isApiSuccess(code) {
  return code === 0 || code === 200;
}

service.interceptors.response.use(
  (response) => {
    const res = response.data;

    if (response.config?._leaguesHtmlGuard && typeof res === 'string') {
      const out = parseLeaguesStringBody(res);
      if (out && typeof out.then === 'function') {
        return out.then(
          (data) => {
            recordRequestAnalytics(response.config, { ok: true, code: 0 });
            return data;
          },
          (err) => {
            recordRequestAnalytics(response.config, { ok: false, code: null });
            return Promise.reject(err);
          }
        );
      }
      recordRequestAnalytics(response.config, { ok: true, code: 0 });
      return out;
    }

    if (!isApiSuccess(res.code)) {
      recordRequestAnalytics(response.config, { ok: false, code: res.code });
      const detail = res.msg || res.message || 'Error';
      const codeNum = Number(res.code);
      // 注册封控 810/811：不要在文案后拼「(810)」，避免用户误以为服务器故障
      const skipCodeSuffix = codeNum === 810 || codeNum === 811;
      const suffix =
        !skipCodeSuffix &&
        res.code != null &&
        res.code !== 200 &&
        res.code !== 0 &&
        !String(detail).includes(String(res.code))
          ? ` (${res.code})`
          : '';
      const apiError = new Error(`${detail}${suffix}`);
      apiError.code = res.code;
      if (res.data !== undefined) {
        apiError.data = res.data;
      }
      attachRequestId(apiError, response.config, response.headers);

      // 业务码 401（HTTP 仍可能是 200）：与 HTTP 401 对齐，尝试 refresh，失败则清 session
      // 联赛请求带 _skipAuthRefresh，避免公开榜页因 /me 失败把整站踢下线
      const cfg = response.config || {};
      if (Number(res.code) === 401 && !cfg._skipAuthRefresh && !cfg._retried && getAccessToken()) {
        cfg._retried = true;
        return (async () => {
          try {
            if (isRefreshing) {
              await new Promise((resolve, reject) => {
                pendingQueue.push({ resolve, reject });
              });
            } else {
              isRefreshing = true;
              try {
                const newToken = await doRefresh();
                flushQueue(newToken, null);
              } catch (refreshError) {
                flushQueue(null, refreshError);
                throw refreshError;
              } finally {
                isRefreshing = false;
              }
            }
            cfg.headers = cfg.headers || {};
            cfg.headers.Authorization = normalizeAccessToken(getAccessToken());
            return service(cfg);
          } catch {
            console.error('[auth] business 401 refresh failed, session expired', {
              requestId: apiError.requestId,
            });
            handleSessionExpired();
            return Promise.reject(apiError);
          }
        })();
      }

      return Promise.reject(apiError);
    }
    recordRequestAnalytics(response.config, { ok: true, code: res.code });
    // 布尔 data（如撤单 { data: false }）必须原样返回，不能走 truthy 判断
    if (Object.prototype.hasOwnProperty.call(res, 'data') && typeof res.data === 'boolean') {
      return res.data;
    }
    if (res.data !== undefined && res.data !== null) {
      return res.data;
    }
    if (Array.isArray(res.rows)) {
      return { rows: res.rows, total: res.total ?? 0 };
    }
    return res;
  },
  async (error) => {
    const originalConfig = error.config;
    const status = error.response?.status;

    if (originalConfig?._leaguesHtmlGuard) {
      recordRequestAnalytics(originalConfig, { ok: false, code: status ?? null });
      return formatLeaguesHttpError(error);
    }

    // 401：access token 过期，尝试刷新（Privy 登录等显式传 token 的请求跳过）
    if (status === 401 && !originalConfig._retried && !originalConfig._skipAuthRefresh) {
      originalConfig._retried = true;

      if (isRefreshing) {
        // 已有刷新在进行，加入等待队列
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalConfig.headers.Authorization = newToken;
          return service(originalConfig);
        });
      }

      isRefreshing = true;
      try {
        const newToken = await doRefresh();
        flushQueue(newToken, null);
        originalConfig.headers.Authorization = newToken;
        return service(originalConfig);
      } catch (refreshError) {
        console.error('[auth] token refresh failed, session expired');
        flushQueue(null, refreshError);
        handleSessionExpired();
        recordRequestAnalytics(originalConfig, { ok: false, code: status ?? null });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    recordRequestAnalytics(originalConfig, {
      ok: false,
      code: status ?? error?.code ?? null,
    });
    attachRequestId(error, originalConfig, error.response?.headers);
    console.error(
      'Response Error:',
      error,
      error?.requestId ? { requestId: error.requestId } : undefined
    );
    return Promise.reject(error);
  }
);

// 4. 封装常用的请求方法
const request = {
  get(url, params, config = {}) {
    return service.get(url, { params, ...config });
  },
  post(url, data, config) {
    return service.post(url, data, config);
  },
  put(url, data, config) {
    return service.put(url, data, config);
  },
  delete(url, params) {
    return service.delete(url, { params });
  },
  patch(url, data) {
    return service.patch(url, data);
  },
};

export default request;
